import { Button } from "antd";
import {
  ArrowRight,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  Wallet,
} from "lucide-react";
import { Fragment, type MouseEvent } from "react";
import { useNavigate } from "react-router";
import type { TAppliedJob } from "../../../app/features/recruitment/recruitment.types";
import useDiscloser from "../../../hooks/useDiscloser";
import cn from "../../../lib/cn";
import { formatDate } from "../../../utils/date";
import InterviewDetails from "./InterviewDetails";
import { MetaItem, OrgAvatar, panelClass } from "./portal";
import { formatExperience, formatSalary } from "./portal.utils";
import { STAGES, getStage } from "./stages";
import { PATHS } from "../../../routes/paths";

type ModalType = "INTERVIEW_DETAILS";

export const StageBadge = ({ stage }: { stage?: string }) => {
  const config = getStage(stage);
  if (!config) return null;
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
        config.tone,
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", config.dot)} />
      {config.label}
    </span>
  );
};

/** Screening ✓ — Assessment ● — Interview ○ — Offer ○ */
const StageStepper = ({ stage }: { stage?: string }) => {
  const current = STAGES.findIndex((s) => s.value === getStage(stage)?.value);
  return (
    <ol className="flex items-start" aria-label="Application progress">
      {STAGES.map((s, index) => {
        const done = index < current;
        const isCurrent = index === current;
        return (
          <Fragment key={s.value}>
            <li
              className="flex w-16 shrink-0 flex-col items-center gap-1.5 sm:w-20"
              aria-current={isCurrent ? "step" : undefined}
            >
              <span
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-full transition-colors",
                  done && "bg-theme-primary text-white",
                  isCurrent &&
                    "border-2 border-theme-primary bg-theme-primary/10",
                  !done && !isCurrent && "border border-gray-300 bg-white",
                )}
              >
                {done && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                {isCurrent && (
                  <span className="h-2 w-2 rounded-full bg-theme-primary" />
                )}
              </span>
              <span
                className={cn(
                  "text-center text-[11px] sm:text-xs",
                  isCurrent
                    ? "font-semibold text-gray-900"
                    : done
                      ? "text-gray-600"
                      : "text-gray-400",
                )}
              >
                {s.label}
              </span>
            </li>
            {index < STAGES.length - 1 && (
              <span
                aria-hidden
                className={cn(
                  "mt-3 h-px min-w-3 flex-1",
                  index < current ? "bg-theme-primary" : "bg-gray-200",
                )}
              />
            )}
          </Fragment>
        );
      })}
    </ol>
  );
};

export const AppliedJobCard = ({ job }: { job: TAppliedJob }) => {
  const navigate = useNavigate();
  const [config, handleOpen, handleClose] = useDiscloser<ModalType>();
  const stage = getStage(job.stage);

  const handleAction = (e: MouseEvent) => {
    e.stopPropagation();
    if (stage?.value === "assessment")
      navigate(PATHS.jobPortal.assessment(job.job_application_id), {
        state: {
          job_title: job.job_title,
          organization_name: job.organization_name,
        },
      });
    else if (stage?.value === "interview")
      handleOpen({ type: "INTERVIEW_DETAILS" });
    else if (stage?.value === "offer") navigate(PATHS.jobPortal.offers);
  };

  return (
    <>
      <article
        onClick={() =>
          navigate(`${PATHS.jobPortal.job(job.job_id)}?from=applications`)
        }
        className={cn(
          panelClass,
          "group cursor-pointer overflow-hidden transition-colors hover:border-theme-primary/40",
        )}
      >
        <div className="flex gap-4 p-5 sm:p-6">
          <OrgAvatar name={job.organization_name} />

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="line-clamp-2 text-base font-semibold text-gray-900 transition-colors group-hover:text-theme-primary sm:truncate">
                  {job.job_title}
                </h3>
                <p className="mt-0.5 truncate text-sm text-gray-500">
                  {job.organization_name}
                  {job.department_name && ` · ${job.department_name}`}
                </p>
              </div>
              <StageBadge stage={job.stage} />
            </div>

            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
              <MetaItem icon={BriefcaseBusiness}>
                {formatExperience(job.job_experience)}
              </MetaItem>
              <MetaItem icon={Wallet}>
                {formatSalary(job.salary_min_bdt, job.salary_max_bdt)}
              </MetaItem>
              <MetaItem icon={CalendarDays}>
                Applied {formatDate(job.applied_at)}
              </MetaItem>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="flex flex-col gap-4 border-t border-gray-100 bg-gray-50/60 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="-m-1 min-w-0 overflow-x-auto p-1 sm:max-w-md sm:flex-1">
            <StageStepper stage={job.stage} />
          </div>
          {stage?.action ? (
            <Button
              onClick={handleAction}
              className="btn-primary h-9 shrink-0 rounded-full px-4 text-[13px] font-medium"
            >
              {stage.action}
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <p className="text-[13px] text-gray-500">
              The hiring team is reviewing your application.
            </p>
          )}
        </div>
      </article>

      <InterviewDetails
        handleClose={handleClose}
        open={config.open && config.type === "INTERVIEW_DETAILS"}
        job_application_id={job.job_application_id}
      />
    </>
  );
};
